import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Trash2, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function CommunityPostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/community/posts/${id}`),
        api.get(`/community/posts/${id}/comments`),
      ]);
      setPost(a.data);
      setComments(b.data);
    } catch (_) {
      toast.error("Could not load post");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, user?.id]);

  const toggleLike = async () => {
    if (!user) return toast.error("Sign in to like posts");
    try {
      const { data } = await api.post(`/community/posts/${id}/like`);
      setPost((p) => ({ ...p, liked: data.liked, likes_count: data.likes_count }));
    } catch (_) {}
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/community/posts/${id}/comments`, { content: text });
      setText("");
      toast.success("Comment posted");
      load();
    } catch (_) {
      toast.error("Could not post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const removeComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      toast.success("Comment deleted");
      load();
    } catch (_) {
      toast.error("Could not delete comment");
    }
  };

  if (loading) return (
    <div className="pt-32 text-center text-zinc-500 font-mono">Loading post...</div>
  );
  if (!post) return (
    <div className="pt-32 text-center text-zinc-500 font-mono">Post not found.</div>
  );

  return (
    <div className="pt-28 pb-20 max-w-3xl mx-auto px-6">
      <Link
        to="/community"
        className="inline-flex items-center gap-2 text-sm font-mono text-zinc-400 hover:text-yellow-400 transition mb-8"
        data-testid="back-to-community"
      >
        <ArrowLeft className="w-4 h-4" /> Back to feed
      </Link>

      {/* Post */}
      <article className="bg-[#0a0a0b] border border-white/10 p-8" data-testid="community-post-detail">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm">
            {(post.author_name || "U")[0].toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-white">{post.author_name}</div>
            <div className="text-xs font-mono text-zinc-500">
              {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>

        <h1 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight">
          {post.title}
        </h1>

        <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap break-all">{post.content}</p>

        <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-6">
          <button
            onClick={toggleLike}
            data-testid="post-detail-like-button"
            className={`flex items-center gap-2 transition ${post.liked ? "text-red-400" : "text-zinc-400 hover:text-red-400"}`}
          >
            <Heart className={`w-5 h-5 ${post.liked ? "fill-current" : ""}`} />
            <span className="font-mono text-sm">{post.likes_count}</span>
          </button>
          <div className="flex items-center gap-2 text-zinc-500">
            <MessageCircle className="w-5 h-5" />
            <span className="font-mono text-sm">{comments.length} comments</span>
          </div>
        </div>
      </article>

      {/* Comments */}
      <section className="mt-10">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-6">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h3>

        {user ? (
          <form
            onSubmit={submitComment}
            className="bg-[#0a0a0b] border border-white/10 p-5 mb-6"
            data-testid="comment-form"
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add your thoughts..."
              rows={3}
              data-testid="comment-input"
              className="w-full bg-transparent text-zinc-200 placeholder:text-zinc-500 resize-none focus:outline-none"
            />
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
              <span className="text-xs font-mono text-zinc-500">Posting as <span className="text-zinc-300">{user.name}</span></span>
              <button
                type="submit"
                disabled={submitting || !text.trim()}
                data-testid="comment-submit"
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-4 py-2 text-sm hover:bg-yellow-500 transition disabled:opacity-50"
              >
                {submitting ? "Posting..." : "Post Comment"}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-[#0a0a0b] border border-white/10 p-5 mb-6 text-center">
            <p className="text-sm text-zinc-400">
              <Link to="/login" className="text-yellow-400 hover:underline">Sign in</Link> to leave a comment.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {comments.length === 0 ? (
            <p className="text-zinc-500 font-mono text-sm text-center py-8 border border-white/5 bg-[#0a0a0b]">
              No comments yet. Be the first.
            </p>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                className="bg-[#0a0a0b] border border-white/10 p-5"
                data-testid={`comment-${c.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-zinc-800 text-zinc-300 font-bold flex items-center justify-center text-xs">
                      {(c.author_name || "U")[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-white">{c.author_name}</span>
                    <span className="text-xs font-mono text-zinc-500">
                      · {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  {user && (user.id === c.author_id || user.role === "admin") && (
                    <button
                      onClick={() => removeComment(c.id)}
                      data-testid={`comment-delete-${c.id}`}
                      className="text-zinc-500 hover:text-red-400 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-zinc-300 leading-relaxed pl-9 break-all">{c.content}</p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}